<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog
      as="div"
      :open="isOpen"
      class="relative z-[999]"
      @close="isOpen = false"
    >
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black bg-opacity-50" />
      </TransitionChild>

      <div
        class="fixed inset-0 flex h-full w-full items-center justify-center p-4"
      >
        <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0 scale-95"
          enter-to="opacity-100 scale-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100 scale-100"
          leave-to="opacity-0 scale-95"
        >
          <DialogPanel
            class="flex h-auto max-h-full w-[818px] flex-col overflow-x-hidden bg-white py-5 shadow-xl transition-all md:py-10"
          >
            <DialogTitle
              class="mb-4 flex items-center px-4 text-2xl font-medium capitalize sm:px-7.5 md:mb-7.5 md:px-12.5"
            >
              Write A REVIEW
              <UiButton
                variant="clear"
                variant-type="icon"
                class="ml-auto"
                @click="isOpen = false"
              >
                <UiIcon name="close" class="h-6 w-6 text-gray-elements" />
              </UiButton>
            </DialogTitle>

            <div
              class="flex w-full flex-auto flex-col overflow-y-auto overflow-x-hidden px-4 sm:px-7.5 md:px-12.5"
            >
              <div>
                <div
                  class="mb-6 flex w-full items-start gap-5 border-b border-gray-strokes pb-5"
                >
                  <div class="mt-1 w-10 shrink-0 md:w-[65px]">
                    <div class="aspect-h-1 aspect-w-1">
                      <UiLazyImg
                        :src="product.image || '/images/no-image.webp'"
                        :alt="
                          product.currentTranslation?.title || product.title
                        "
                      />
                    </div>
                  </div>
                  <div
                    class="flex flex-auto flex-col self-stretch overflow-hidden"
                  >
                    <div class="truncate font-medium">
                      {{ product.title }}
                    </div>

                    <div class="flex-auto text-sm text-gray-details">
                      <div
                        v-if="product?.embedded"
                        v-html="product?.short_description"
                      ></div>
                      <template v-else>
                        <div
                          v-for="(material, idx) in product.materials"
                          :key="idx"
                        >
                          {{ material.material_category_title }}:
                          {{ material.title }}
                        </div>
                      </template>
                    </div>
                  </div>
                </div>

                <form @submit.prevent="onSubmit">
                  <div
                    class="mb-7.5 flex flex-col gap-x-12.5 gap-y-4 md:flex-row"
                  >
                    <div class="shrink-0">
                      <div class="mb-2.5 font-medium">Your Rating*</div>
                      <UiRatingControls
                        v-model="formValues.stars"
                        class="w-full md:w-min"
                        name="stars"
                      />
                    </div>
                    <div class="flex flex-1 shrink-0 flex-col gap-y-2">
                      <UiInput
                        v-model="formValues.title"
                        label="Title for your review*"
                        placeholder="Write a title"
                        name="title"
                      />

                      <div class="flex flex-col gap-x-9 gap-y-3 md:flex-row">
                        <p class="font-normal">
                          Do you recommend this product?
                        </p>
                        <div class="flex gap-6">
                          <UiRadio
                            v-model="formValues.recommend"
                            name="recommend"
                            label="Yes"
                            hide-details
                            value="1"
                          />
                          <UiRadio
                            v-model="formValues.recommend"
                            name="recommend"
                            label="No"
                            hide-details
                            value="0"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="mb-4 w-full md:mb-13">
                    <div class="mb-4 text-lg font-medium">About You</div>

                    <div
                      class="mb-1 flex w-full flex-col gap-x-8 gap-y-1 md:flex-row"
                    >
                      <UiInput
                        v-model="formValues.author"
                        name="author"
                        class="w-full"
                        placeholder="Jane Doe"
                        label="Name*"
                      />
                      <UiInput
                        v-model="formValues.email"
                        name="email"
                        class="w-full"
                        placeholder="example@email.com"
                        label="Email*"
                      />
                    </div>
                    <UiTextarea
                      v-model="formValues.text"
                      name="text"
                      placeholder="How was your experience?"
                    />
                  </div>

                  <div
                    v-if="imagePreview.length"
                    class="mb-3 inline-flex px-4 py-2.5 font-normal"
                    :class="[
                      errors.medias || imagePreview.length >= 5
                        ? 'bg-error-bg text-error-text'
                        : 'bg-pink-light text-button-primary',
                    ]"
                  >
                    <span v-if="errors.medias">
                      {{ errors.medias }}
                    </span>
                    <span v-else>
                      <b class="font-medium">{{
                        `${imagePreview.length}/${FILE_LIMIT}`
                      }}</b>
                      {{ imageSelectedText }}
                    </span>
                  </div>
                  <div
                    class="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5"
                  >
                    <div
                      v-for="(src, idx) in imagePreview"
                      :key="idx"
                      class="relative w-full"
                    >
                      <button
                        class="absolute right-1.5 top-1.5 z-[1]"
                        type="button"
                        @click="remove(idx)"
                      >
                        <UiIcon
                          name="close"
                          class="h-6 w-6 text-gray-elements"
                        />
                      </button>
                      <div class="aspect-h-1 aspect-w-1">
                        <UiLazyImg
                          :src="src"
                          class="object-cover"
                          cover
                          :alt="
                            product.currentTranslation?.title || product.title
                          "
                          @error="$event.target.src = '/images/no-image.webp'"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="flex flex-col gap-x-20 gap-y-6 md:flex-row">
                    <UiInputFile
                      name="medias"
                      multiple
                      :accept="getAllowedExt('image')"
                      class="inline-flex shrink-0 cursor-pointer justify-center gap-x-1.5 border-b border-primary py-2 font-medium text-primary"
                      :limit="FILE_LIMIT"
                      :disabled="imagePreview.length >= FILE_LIMIT"
                      @change="onFileInputChange"
                    >
                      Upload your Picture
                      <UiIcon name="add_file" class="h-6 w-6" />
                    </UiInputFile>

                    <UiButton
                      color="primary"
                      type="submit"
                      class="flex-auto"
                      :loading="isLoading"
                    >
                      Submit Review
                    </UiButton>
                  </div>
                </form>
              </div>
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import {
  Dialog,
  DialogPanel,
  TransitionRoot,
  TransitionChild,
  DialogTitle,
} from "@headlessui/vue";
import { array, number, object, string } from "yup";
import { useForm } from "vee-validate";
import { ProductData } from "ecom-types";
import { FetchError } from "ofetch";

const WORDS_MAX_LENGTH = 200;
const FILE_LIMIT = 5;
const generalStore = useGeneralStore();

const props = defineProps<{
  modelValue: boolean;
  product: ProductData;
}>();

const { $api } = useNuxtApp();
const emit = defineEmits(["update:modelValue", "success"]);

interface InitialForm {
  stars: number;
  title: string;
  recommend: number;
  author: string;
  email: string;
  text: string;
  medias: File[];
}
const initialForm: InitialForm = {
  stars: 0,
  title: "",
  recommend: 1,
  author: generalStore.userInfo ? generalStore.userInfo.first_name : "",
  email: generalStore.userInfo ? generalStore.userInfo.email : "",
  text: "",
  medias: [] as File[],
};

const formValues = reactive(
  JSON.parse(JSON.stringify(initialForm)),
) as InitialForm;

const descriptionWords = computed(() => {
  return formValues.text.split(/\s+/).filter(Boolean).length;
});

const imagePreview = computed(() => {
  return (
    formValues?.medias?.map?.((file) => {
      return URL.createObjectURL(file);
    }) || []
  );
});
const imageSelectedText = computed(() => {
  return imagePreview.value.length >= FILE_LIMIT
    ? `Image limit reached`
    : `Images Uploaded`;
});

const isLoading = ref(false);

const isOpen = computed<boolean>({
  set(val) {
    emit("update:modelValue", val);
  },
  get() {
    return props.modelValue;
  },
});

watch(isOpen, (value) => {
  if (value) {
    resetForm({
      values: initialForm,
    });
    Object.assign(formValues, JSON.parse(JSON.stringify(initialForm)));
  }
});

const schema = object().shape({
  stars: number().moreThan(0, "Please indicate your rating").label("Rating"),
  title: string().required().max(255).label("Title"),
  author: string().required().max(255).label("Name"),
  email: string().email().required().label("Email"),
  text: string()
    .nullable(),
  medias: array<File[]>()
    .max(FILE_LIMIT, `Limit ${FILE_LIMIT} files`)
    .test(
      "is-correct-file",
      "Only the following formats are accepted: .jpeg, .jpg, .png",
      checkIfFilesAreCorrectType,
    ),
});
const { handleSubmit, errors, resetForm, setErrors, setFieldValue } = useForm({
  initialValues: formValues,
  validationSchema: schema,
});

const onSubmit = handleSubmit(async (values) => {
  if (!props.product.id) return;
  isLoading.value = true;
  const params = {
    product_id: props.product.id,
    ...values,
  } as const;

  const formData = new FormData();

  Object.keys(params).forEach((key) => {
    if (isArray(params[key])) {
      params[key].forEach((item) => {
        formData.append(`${key}[]`, item);
      });
    } else {
      formData.append(key, params[key]);
    }
  });

  try {
    await $api.reviews.leaveReviews(formData);
    isLoading.value = false;

    isOpen.value = false;
    emit("success");
  } catch (error) {
    if (error instanceof FetchError && isArray(error?.data?.errors)) {
      setErrors(responseErrorToObjectErrors(error?.data?.errors));
    } else {
      console.error(error);
    }
  }
});

function remove(i: number) {
  formValues.medias = formValues.medias.filter((file, idx) => idx !== i);

  setFieldValue("medias", formValues.medias);
}

function onFileInputChange(files: File[]) {
  const arr = [...formValues.medias, ...files];

  formValues.medias = arr.length <= FILE_LIMIT ? arr : arr.slice(0, FILE_LIMIT);

  setFieldValue("medias", formValues.medias);
}
</script>
